import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
export type TypographyType =
  | 'super'
  | 'header'
  | 'preview-header'
  | 'subheader'
  | 'subheader-small'
  | 'title'
  | 'big-title'
  | 'small'
  | 'smaller'
  | 'paragraph'
  | 'link'
  | 'pricing-price'
  | 'circle'
  | 'circle-fill'
  | 'circle-success'
  | 'circle-grey'
  | 'circle-black'
  | 'small-circle-black'
  | 'circle-error'
  | 'filled-circle'
  | 'filled-circle-dark'
  | 'section-title'
  | 'section-title-preview'
  | 'input-number'
  | 'analytics-nav'
  | '';
export type customFontType =
  | 'light'
  | 'medium'
  | 'semi-bold'
  | 'bold'
  | 'everett-regular'
  | 'everett-medium'
  | 'everett-bold';
export interface TypographyProps {
  id?: string;
  type?: TypographyType;
  children?: string | React.ReactNode;
  className?: string;
  weight?: string;
  color?: string;
  opacity?: string;
  capitalize?: boolean;
  hasHover?: boolean;
  uppercase?: boolean;
  lineThrough?: boolean;
  margin?: string;
  align?: 'left' | 'center' | 'right';
  small?: boolean;
  isPreview?: boolean;
  hasTruncate?: boolean;
  customSize?: string;
  customFont?: customFontType;
  isGrey?: boolean;
  style?: React.CSSProperties;
  ref?: React.LegacyRef<HTMLSpanElement>;
  onClick?: (ev: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
}
export const Typography: React.FC<TypographyProps> = (props) => (
  <span
    id={props.id ? props.id : undefined}
    className={props.className}
    onClick={(ev: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
      props.onClick ? props.onClick(ev) : null
    }
    style={{ ...props.style }}
    ref={props.ref}
  >
    {props.children}
  </span>
);
const Super = (props: TypographyProps) =>
  props.type === 'super'
    ? css`
        font-size: ${({ theme }) => theme.typography.h1};
        line-height: calc(${({ theme }) => theme.typography.h1} + 10px);
        letter-spacing: -2.5px;
        text-align: center;
        font-weight: 700;
        @media ${({ theme }) => theme.device.tablet} {
          font-size: ${({ theme }) => theme.typography.super};
          line-height: calc(${({ theme }) => theme.typography.super} + 10px);
        }
      `
    : '';
const Header = (props: TypographyProps) =>
  props.type === 'header'
    ? css`
        font-size: ${({ theme }) => theme.typography.h1Small};
        line-height: ${({ theme }) => theme.typography.h1Small};
        @media ${({ theme }) => theme.device.mobileL} {
          font-size: ${({ theme }) => theme.typography.h1};
          line-height: ${({ theme }) => theme.typography.h1};
        }
      `
    : '';
const HeaderSmall = (props: TypographyProps) =>
  props.type === 'header' && props.small
    ? css`
        font-size: ${({ theme }) => theme.typography.h1Small};
        line-height: ${({ theme }) => theme.typography.h1Small};
        @media ${({ theme }) => theme.device.mobileL} {
          font-size: ${({ theme }) => theme.typography.h1Small};
          line-height: ${({ theme }) => theme.typography.h1Small};
        }
      `
    : '';
const PreviewHeader = (props: TypographyProps) =>
  props.type === 'preview-header'
    ? css`
        font-size: ${({ theme }) => theme.typography.h3};
        line-height: ${({ theme }) => theme.typography.h2};
        @media ${({ theme }) => theme.device.tablet} {
          font-size: ${({ theme }) => theme.typography.h1Small};
          line-height: ${({ theme }) => theme.typography.h1};
        }
      `
    : '';
const AnalyticsNav = (props: TypographyProps) =>
  props.type === 'analytics-nav'
    ? css`
        font-weight: 400;
        font-size: ${({ theme }) => theme.typography.sectionTitle};
        @media ${({ theme }) => theme.device.tablet} {
          font-size: ${({ theme }) => theme.typography.h2};
        }
      `
    : '';
const Subheader = (props: TypographyProps) =>
  props.type === 'subheader'
    ? css`
        font-weight: 700;
        font-size: ${({ theme }) => theme.typography.h2};
      `
    : '';
const SubheaderSmall = (props: TypographyProps) =>
  props.type === 'subheader-small'
    ? css`
        font-weight: 400;
        font-size: ${({ theme }) => theme.typography.sectionTitle};
        @media ${({ theme }) => theme.device.tablet} {
          font-size: ${({ theme }) => theme.typography.h2Small};
        }
      `
    : '';
const Title = (props: TypographyProps) =>
  props.type === 'title'
    ? css`
        font-weight: 700;
        font-size: ${({ theme }) => theme.typography.h3};
      `
    : '';
const Paragraph = (props: TypographyProps) =>
  props.type === 'paragraph'
    ? css`
        opacity: 60%;
        font-size: ${({ theme }) => theme.typography.span};
      `
    : '';
const Small = (props: TypographyProps) =>
  props.type === 'small'
    ? css`
        color: ${props.color || `${({ theme }) => theme.colors.primary}`};
        font-size: ${({ theme }) => theme.typography.span};
      `
    : '';
const Smaller = (props: TypographyProps) =>
  props.type === 'smaller'
    ? css`
        color: ${props.color ||
        css`
          ${({ theme }) => theme.colors.primary}
        `};
        font-size: ${({ theme }) => theme.typography.span};
        @media ${({ theme }) => theme.device.laptop} {
          font-size: ${({ theme }) => theme.typography.small};
        }
      `
    : '';
const Link = (props: TypographyProps) =>
  props.type === 'link'
    ? css`
        cursor: pointer;
        font-weight: 700;
        text-decoration: none;
        transition: color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};
        &:hover {
          color: ${({ theme }) => theme.colors.primary};
        }
      `
    : '';
const SectionTitle = (props: TypographyProps) =>
  props.type === 'section-title'
    ? css`
        @media ${({ theme }) => theme.device.mobileL} {
          font-size: ${({ theme }) => theme.typography.sectionTitle};
        }
      `
    : '';
const SectionTitlePreview = (props: TypographyProps) =>
  props.type === 'section-title-preview'
    ? css`
        font-size: ${({ theme }) => theme.typography.span};
        @media ${({ theme }) => theme.device.laptop} {
          font-size: ${({ theme }) => theme.typography.sectionTitle};
        }
      `
    : '';
const PricingPrice = (props: TypographyProps) =>
  props.type === 'pricing-price'
    ? css`
        font-size: ${({ theme }) => theme.typography.h2};
        @media ${({ theme }) => theme.device.laptop} {
          font-size: ${({ theme }) => theme.typography.h2Bigger};
        }
      `
    : '';
const BigTitle = (props: TypographyProps) =>
  props.type === 'big-title'
    ? css`
        font-size: ${({ theme }) => theme.typography.sectionTitle};
      `
    : '';
const circleStyle = css`
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.small};
  position: relative;
  z-index: ${({ theme }) => theme.zIndices.normal};
  text-align: center;
  width: 24px;
  height: 24px;
  line-height: 24px;
  user-select: none;
  transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  &:after {
    content: '';
    width: 24px;
    height: 24px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: 2px solid ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    box-sizing: border-box;
    transition: border
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }
`;
const Circle = (props: TypographyProps) =>
  props.type === 'circle'
    ? css`
        ${circleStyle}
      `
    : '';
const CircleFill = (props: TypographyProps) =>
  props.type === 'circle-fill'
    ? css`
        ${circleStyle}
        background-color: ${({ theme }) => theme.colors.primary};
        color: ${({ theme }) => theme.colors.differentBackground};
        &:after {
          border-color: ${({ theme }) => theme.colors.primary};
        }
      `
    : '';
const CircleSuccess = (props: TypographyProps) =>
  props.type === 'circle-success'
    ? css`
        ${circleStyle};
        color: ${({ theme }) => theme.colors.success};
        &:after {
          border-color: ${({ theme }) => theme.colors.success};
        }
      `
    : '';
const CircleError = (props: TypographyProps) =>
  props.type === 'circle-error'
    ? css`
        ${circleStyle};
        color: ${({ theme }) => theme.colors.error};
        &:after {
          border-color: ${({ theme }) => theme.colors.error};
        }
      `
    : '';
const CircleGrey = (props: TypographyProps) =>
  props.type === 'circle-grey'
    ? css`
        ${circleStyle};
        color: ${({ theme }) => theme.colors.border};
        &:after {
          border-color: ${({ theme }) => theme.colors.border};
        }
      `
    : '';
const CircleBlack = (props: TypographyProps) =>
  props.type === 'circle-black'
    ? css`
        ${circleStyle};
        color: ${({ theme }) => theme.colors.primary};
        font-size: ${({ theme }) => theme.typography.span};
        width: 24px;
        height: 24px;
        line-height: 24px;
        &:after {
          border-color: ${({ theme }) => theme.colors.primary};
          width: 24px;
          height: 24px;
        }
      `
    : '';

const SmallCircleBlack = (props: TypographyProps) =>
  props.type === 'small-circle-black'
    ? css`
        ${circleStyle};
        color: ${({ theme }) => theme.colors.primary};
        font-size: ${({ theme }) => theme.typography.small};
        width: 24px;
        height: 24px;
        line-height: 24px;

        &:after {
          border-color: ${({ theme }) => theme.colors.primary};
          width: 24px;
          height: 24px;
        }
      `
    : '';

const FilledCircleStyle = css`
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.span};
  position: relative;
  z-index: ${({ theme }) => theme.zIndices.normal};
  text-align: center;
  width: 24px;
  height: 24px;
  line-height: 24px;
  box-sizing: border-box;
  cursor: default;
  user-select: none;
  &:after {
    content: '';
    width: 24px;
    height: 24px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: ${({ theme }) => theme.colors.border};
    border-radius: 50%;
    box-sizing: border-box;
    z-index: -1;
    opacity: 0.2;
  }
`;
const FilledCircleStyleDark = css`
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.span};
  position: relative;
  z-index: ${({ theme }) => theme.zIndices.normal};
  text-align: center;
  width: 24px;
  height: 24px;
  line-height: 24px;
  box-sizing: border-box;
  cursor: default;
  user-select: none;
  color: ${({ theme }) => theme.colors.background};
  &:after {
    content: '';
    width: 16px;
    height: 16px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    box-sizing: border-box;
    z-index: -1;
    opacity: 1;
  }
`;
const FilledCircle = (props: TypographyProps) =>
  props.type === 'filled-circle'
    ? css`
        ${FilledCircleStyle}
      `
    : '';
const FilledCircleDark = (props: TypographyProps) =>
  props.type === 'filled-circle-dark'
    ? css`
        ${FilledCircleStyleDark}
      `
    : '';
const InputNumber = (props: TypographyProps) =>
  props.type === 'input-number'
    ? css`
        font-weight: 700;
        color: ${({ theme }) => rgba(theme.colors.header, 0.3)};
      `
    : '';
export default styled(Typography)`
  line-height: 1.2;
  font-size: ${({ theme }) => theme.typography.h4};
  display: block;
  color: ${(props) =>
    props.color ||
    css`
      ${({ theme }) => theme.colors.primary}
    `};
  display: block;
  ${(props) =>
    props.type === 'header' && !props.small ? 'margin-bottom: 42px' : null};
  ${(props) => (props.uppercase ? `text-transform: uppercase;` : null)}
  ${(props) => (props.capitalize ? `text-transform: capitalize;` : null)}
  ${(props) => (props.opacity ? `opacity: ${props.opacity};` : null)}
  ${(props) => (props.align ? `text-align: ${props.align};` : null)}
  ${(props) => (props.margin ? `margin: ${props.margin};` : null)}
  ${(props) =>
    props.isPreview
      ? css`
          font-size: ${({ theme }) => theme.typography.span};
          @media ${({ theme }) => theme.device.tablet} {
            font-size: ${({ theme }) => theme.typography.h4};
          }
        `
      : null}
  ${Super}
  ${Header}
  ${PreviewHeader}
  ${HeaderSmall}
  ${Subheader}
  ${SubheaderSmall}
  ${Title}
  ${Paragraph}
  ${Small}
  ${Smaller}
  ${Link}
  ${PricingPrice}
  ${SectionTitle}
  ${SectionTitlePreview}
  ${BigTitle}
  ${Circle}
  ${CircleFill}
  ${CircleSuccess}
  ${CircleGrey}
  ${CircleError}
  ${CircleBlack}
  ${SmallCircleBlack}
  ${FilledCircle}
  ${FilledCircleDark}
  ${InputNumber}
  ${AnalyticsNav}
  ${(props) =>
    props.align
      ? css`
          text-align: ${props.align};
        `
      : null};
  ${(props) =>
    props.weight
      ? css`
          font-weight: ${props.weight};
        `
      : null};
  ${(props) =>
    props.hasHover
      ? css`
          opacity: 1;
          transition: opacity
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};
          &:hover {
            opacity: 0.5;
          }
        `
      : null};
  ${(props) =>
    props.customSize
      ? css`
          font-size: ${props.customSize};
        `
      : null}
  ${(props) =>
    props.customFont === 'light'
      ? css`
          font-family: 'Urbane-Light', 'Inter', sans-serif;
        `
      : null}
  ${(props) =>
    props.customFont === 'medium'
      ? css`
          font-family: 'Urbane-Medium', 'Inter', sans-serif;
        `
      : null}
  ${(props) =>
    props.customFont === 'semi-bold'
      ? css`
          font-family: 'Urbane-DemiBold', 'Inter', sans-serif;
        `
      : null}
  ${(props) =>
    props.customFont === 'bold'
      ? css`
          font-family: 'Urbane-Bold', 'Inter', sans-serif;
        `
      : null}
        ${(props) =>
    props.customFont === 'everett-regular'
      ? css`
          font-family: 'Everett-Regular', 'Inter', sans-serif;
        `
      : null}
  ${(props) =>
    props.customFont === 'everett-medium'
      ? css`
          font-family: 'Everett-Medium', 'Inter', sans-serif;
        `
      : null}
  ${(props) =>
    props.customFont === 'everett-bold'
      ? css`
          font-family: 'Everett-Bold', 'Inter', sans-serif;
        `
      : null}
  ${(props) =>
    props.hasTruncate
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : null}
  ${(props) =>
    props.lineThrough
      ? css`
          text-decoration: line-through;
        `
      : null}
  ${(props) =>
    props.isGrey
      ? css`
          color: ${({ theme }) => theme.colors.tertiary};
        `
      : null}
`;
